import useToastContext from "hooks/useToastContext";
import jwt_decode from "jwt-decode";
import { createContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { generateRandomID } from "utils/reusableMethods";
import { apiDelete, apiGet, apiPost, apiPut } from "../utils/apiFetch";
import apiPath from "../utils/apiPath";
const AuthContext = createContext();

export default AuthContext;

export const AuthProvider = ({ children }) => {
  const { i18n } = useTranslation();
  let [user, setUser] = useState(() => (localStorage.getItem("token") ? jwt_decode(localStorage.getItem("token")) : null));
  let [wallets, setWallets] = useState(null);
  let [accounts, setAccounts] = useState([]);
  let [hasNewNotification, setHasNewNotification] = useState(false);

  const navigate = useNavigate();
  const notification = useToastContext();
  const [lastActivity, setLastActivity] = useState(new Date());
  const [language, setLanguage] = useState(localStorage.getItem("language") === "en" ? 1 : 2);

  let loginUserSmartfin = async (body) => {
    const { data } = await apiPost(apiPath.loginUserSmartfin, body);
    if (data.success) {
      if (data?.results?.otpID) {
        navigate("/login/otp", {
          state: {
            otpID: data?.results?.otpID,
          },
        });
      } else {
        var needsRegistration = data?.results?.first_name != null;
        return {
          message: "",
          needsRegistration: needsRegistration,
          registrationData: data?.results,
        };
      }
    } else {
      return {
        message: data.message,
        needsRegistration: false,
        registrationData: null,
      };
    }
  };

  let loginWithSmartfin = async (body) => {

    const { data } = await apiPost(apiPath.loginWithSmartfin, body)
    if (data.success) {
      notification.success(data.message);
      navigate("/login/otp", {
        state: {
          otpID: data?.results?.otpID,
          email: body?.email,
          mobile: body?.mobile,
          password: body?.password,
          username: body?.username,
        },
      });
    } else {
      if (data.results != null && data?.results?.is_mobile_verified === 0 && data?.results?.is_email_verified === 0) {
        navigate("/register/otp", {
          state: {
            country_code: data?.results?.country_code,
            phone: data?.results?.mobile,
            email: data?.results?.email,
          },
        });
      } else {
        notification.error(data.message);
        navigate("/register")
      }
    }
  } 

  let loginUser = async (body) => {
    const { data } = await apiPost(apiPath.loginUser + "?loginType=password", body);
    if (data.success) {
      notification.success(data.message);
      navigate("/login/otp", {
        state: {
          otpID: data?.results?.otpID,
          email: body?.email,
          mobile: body?.mobile,
          password: body?.password,
          username: body?.username,
        },
      });
    } else {
      if (data.results != null && data?.results?.is_mobile_verified === 0 && data?.results?.is_email_verified === 0) {
        navigate("/register/otp", {
          state: {
            country_code: data?.results?.country_code,
            phone: data?.results?.mobile,
            email: data?.results?.email,
          },
        });
      } else {
        notification.error(data.message);
      }
    }
  };

  const updateProfile = async (formData, callback) => {
    try {
      let config = {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      };
      const res = await apiPut(apiPath.updateProfile, formData, config);
      notification.success(res.data.message);
      const token = res?.data?.results?.token || null;
      const refresh_token = res?.data?.results?.refresh_token || null;
      storeTokens(token, refresh_token);
      callback();
    } catch (err) {}
  };

  const storeTokens = (token, refresh_token) => {
    localStorage.setItem("token", token);
    localStorage.setItem("refresh_token", refresh_token);
    setUser(jwt_decode(token));
  };

  const deleteAccountRequest = async () => {
    try {
      const res = await apiDelete(apiPath.deleteAccountRequest);
      if (res.data.success) {
        notification.success(res.data.message);
        logoutUser();
      } else {
        notification.error(res.data.message);
      }
    } catch (err) {}
  };

  const getProfile = async () => {
    try {
      var response = await apiGet(apiPath.getProfile);
      if (response.data.success) {
        setUser((prevValue) =>
          prevValue
            ? {
                ...prevValue,
                profile_pic: response.data.results.profile_pic,
                first_name: response.data.results.first_name,
                last_name: response.data.results.last_name,
                email: response.data.results.email,
                mobile: response.data.results.mobile,
                username: response.data.results.username,
                secondaryMobile: response.data.results.secondaryMobile,
                secondaryCountryCode: response.data.results.secondaryCountryCode,
                secondaryEmail: response.data.results.secondaryEmail,
                notifications: response.data.results.notifications,
              }
            : response.data.results
        );
        setWallets(response.data.results.wallets);
        setAccounts(response.data.results.accounts);
        setHasNewNotification(response.data.results.hasNewNotification);
      }
    } catch (err) {}
  };

  const switchAccount = async (id) => {
    try {
      const requestBody = {
        id,
        device_id: generateRandomID(10),
        device_type: "web",
        device_token: generateRandomID(20),
      };
      const res = await apiPost(apiPath.changeUserAccount, requestBody);
      if (!res.data.success) {
        notification.error(res.data.message);
        return;
      }

      const token = res?.data?.results?.token || null;
      const refresh_token = res?.data?.results?.refresh_token || null;
      setWallets(null);
      setAccounts([]);
      storeTokens(token, refresh_token);
      getProfile();
      notification.success(res.data.message);
      navigate("/");
    } catch (err) {}
  };

  let logoutUser = () => {
    setUser(null);
    setWallets(null);
    setAccounts([]);
    localStorage.removeItem("token");
    localStorage.removeItem("refresh_token");
    navigate("/");
  };

  useEffect(() => {
    if (localStorage.getItem("language") === "en") {
      i18n.changeLanguage("en");
    } else {
      i18n.changeLanguage("al");
    }
    if (user) {
      getProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  let contextData = {
    user,
    setUser,
    getProfile,
    wallets,
    accounts,
    setWallets,
    setAccounts,
    hasNewNotification,
    setHasNewNotification,
    loginUser,
    loginUserSmartfin,
    loginWithSmartfin,
    logoutUser,
    updateProfile,
    deleteAccountRequest,
    switchAccount,
    setLanguage,
    setLastActivity,
    lastActivity,
    storeTokens,
    language,
  };
  return <AuthContext.Provider value={contextData}>{children}</AuthContext.Provider>;
};
